// src/App.tsx
import { AnalyzerOderCheck } from "./components/AnalyzerOderCheck";
import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useUser, useAuth } from "@clerk/clerk-react";
import { loginZiel } from "./lib/loginZiel";
import PlanGuard from "@/components/PlanGuard";
import { ConsentBanner } from "@/components/analytics/ConsentBanner";
import { RouteChangeTracker } from "@/components/analytics/RouteChangeTracker";

// -------------------------------------------------------------
// Loading Screen (statt nacktem "Lade..."-Text)
// -------------------------------------------------------------
export function LoadingScreen({ label = "Wird geladen" }: { label?: string }) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3" style={{ background: "#f5f3ea" }}>
      <div className="relative h-9 w-9">
        <div className="absolute inset-0 rounded-full border-2" style={{ borderColor: "#e3e6da" }} />
        <div
          className="absolute inset-0 animate-spin rounded-full border-2 border-transparent"
          style={{ borderTopColor: "#244737", borderRightColor: "#244737" }}
        />
      </div>
      <div className="flex items-center gap-1 text-sm" style={{ color: "#5e6d60" }}>
        <span>{label}</span>
        <span className="flex gap-0.5">
          <span className="animate-bounce" style={{ animationDelay: "0ms" }}>.</span>
          <span className="animate-bounce" style={{ animationDelay: "120ms" }}>.</span>
          <span className="animate-bounce" style={{ animationDelay: "240ms" }}>.</span>
        </span>
      </div>
    </div>
  );
}

// Auth-Routen (Code-Splitting)
const Login = lazy(() => import("./routes/auth/Login"));
// Register lazy; während des Nachladens zeigt der Fallback dieselbe Headline an
// derselben Stelle (RegisterSkeleton), damit kein Spinner die Seite ersetzt.
const Register = lazy(() => import("./routes/auth/Register"));
const AnmeldenPerLink = lazy(() => import("./routes/auth/AnmeldenPerLink"));
const Logout = lazy(() => import("./routes/auth/Logout"));
const Account = lazy(() => import("./routes/auth/Account"));

// Tool-Routen lazy: ihre Bundles (recharts, jsPDF, html2canvas, framer-motion, ...)
// liegen nicht mehr im Initial-Chunk und werden erst beim Aufruf geladen.
const Compare = lazyRoute(() => import("./routes/Compare"));
const Extension = lazyRoute(() => import("./routes/Extension"));
const Eigentumswohnung = lazyRoute(() => import("./routes/Eigentumswohnung"));
const MFHCheck = lazyRoute(() => import("./routes/MFHCheck"));
const Einfamilienhaus = lazyRoute(() => import("./routes/EinfamilienhausCheck"));
const MixedUseCheck = lazyRoute(() => import("./routes/MixedUseCheck"));
const GewerbeCheck = lazyRoute(() => import("./routes/GewerbeCheck"));
const AfaRechner = lazyRoute(() => import("./routes/AfaRechner"));
const Mietkalkulation = lazyRoute(() => import("./routes/Mietkalkulation"));
const FinanzierungSimple = lazyRoute(() => import("./routes/FinanzierungSimple"));
const FinanzierungsVergleich = lazyRoute(() => import("./routes/FinanzierungsVergleich"));
const Portfolio = lazyRoute(() => import("./routes/Portfolio"));
const Objektvergleich = lazyRoute(() => import("./routes/portfolio/Vergleich"));
// /deal/:id (PHASE-4-BEFUND.md Runde 1): helles Design (src/design) innerhalb der Seite,
// seit Nachtrag 15.10.2026 (Punkt 4) in der AppShell -- siehe DealDetail.tsx/DealShell.tsx.
const DealDetail = lazyRoute(() => import("./routes/DealDetail"));
// Design-Grundlage (neue Tokens/Komponenten), nur per direkter URL erreichbar,
// bewusst nirgendwo verlinkt. Eigener Chunk, laedt fonts.css/tokens.css nur hier.
const DesignPreview = lazyRoute(() => import("./routes/DesignPreview"));
// Schnell-Check (Phase 3): oeffentliche Route ohne RequireLogin, noindex,
// bewusst nirgends verlinkt (siehe Check.tsx/DEAL-FLOW-STATUS.md) -- geht erst
// zusammen mit Phase 4 live.
const Check = lazyRoute(() => import("./routes/Check"));

// Pricing/Checkout/Upgrade/Changelog lazy (PageSpeed 03.11.2026): sie lagen im Start-Bundle,
// das auch der Schnell-Check auf dem Handy laden muss. Checkout wird beim Klick auf
// „Kaufen“ in einem Bruchteil einer Sekunde nachgeladen.
import { lazyRoute } from "./lib/lazyRoute";
const Pricing = lazyRoute(() => import("./routes/Pricing"));
const Checkout = lazyRoute(() => import("./routes/Checkout"));
const Upgrade = lazyRoute(() => import("./routes/Upgrade"));
const Changelog = lazyRoute(() => import("./routes/Changelog"));
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { RegisterSkeleton } from "./components/RegisterSkeleton";
import { registrierungsweg } from "./lib/anmeldeweg";
import { trackSignUp, trackPurchase, trackToolUsed, trackFirstLogin, trackSecondSession } from "./hooks/useTrackingEvents";
import { useLandingAttributionCache } from "./hooks/useLandingAttributionCache";
import { loadQuickCheckDraft } from "./lib/quickCheckDraft";
import { seitenMetaFuer, setzeSeitenMeta } from "./lib/seitenTitel";
// AuthProbe (Diagnose-Seite) lazy: sie zieht den Supabase-Client (~150 KB) ins Start-Bundle.
const AuthProbe = lazyRoute(() => import("./routes/AuthProbe"));
import AppShell from "./components/AppShell";
import { StartUmleitung } from "./components/StartUmleitung";

// Propora Assistent: lazy und auf Register/Login/Checkout nicht geladen
const PropraAssistent = lazy(() => import("./components/Assistent"));
const ASSISTENT_HIDDEN_PREFIXES = ["/register", "/login", "/checkout", "/check"];

export type Module = {
  key: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  /** Nur für die drei Ausnahmen vom binären "alles offen, nur Features gated"-
   *  Modell gesetzt (Objekt-Vergleich, Abschreibungs-Planer) -- diese Tools
   *  sind komplett PRO-only, nicht nur einzelne Features darin. */
  requiredPlan?: "pro";
};

// -------------------------------------------------------------
// Nach Checkout Plan sofort sichtbar machen
// -------------------------------------------------------------
function CheckoutRefresh() {
  const location = useLocation();

  React.useEffect(() => {
    const qp = new URLSearchParams(location.search);
    if (qp.get("checkout") === "success") {
      sessionStorage.removeItem("pending_checkout_plan");
      sessionStorage.removeItem("pending_checkout_interval");
      // Nur PRO ist kaeuflich (success_url liefert immer plan=pro, siehe
      // api/stripe/create-checkout-session.ts) -- "basis" kommt hier nie an.
      const plan = qp.get("plan");
      if (plan === "pro") {
        trackPurchase(plan);
      }
      qp.delete("checkout");
      qp.delete("plan");
      const s = qp.toString();
      const cleanUrl = `${location.pathname}${s ? `?${s}` : ""}`;
      window.history.replaceState({}, "", cleanUrl);
      setTimeout(() => window.location.reload(), 50);
    }
  }, [location.pathname, location.search]);

  return null;
}

function SignupTracker() {
  const { user, isLoaded } = useUser();

  React.useEffect(() => {
    if (!isLoaded || !user) return;
    const createdAt = user.createdAt;
    if (!createdAt) return;

    const ageMs = Date.now() - new Date(createdAt).getTime();
    const isNewUser = ageMs < 60_000;
    const key = `tracked_signup_${user.id}`;

    if (isNewUser && !sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      // Paket 38: echter Registrierungsweg (email | google | …) statt fest "email".
      trackSignUp(registrierungsweg(user.externalAccounts));
    }
  }, [isLoaded, user]);

  return null;
}


// Feuert einmal pro App-Mount first_login/second_session, sobald Clerk einen
// eingeloggten Nutzer bestätigt -- die eigentliche Ersteinschätzung ("ist das
// wirklich der erste Login / der zweite Kalendertag") passiert serverseitig in
// /api/session-status (Postgres-RPC, siehe sql/2026-09-17_user_sessions.sql),
// nicht hier im Client, sonst würde first_login bei jedem neuen Gerät/Browser
// erneut feuern.
function SessionTracker() {
  const { isSignedIn, getToken } = useAuth();
  const fired = React.useRef(false);

  React.useEffect(() => {
    if (!isSignedIn || fired.current) return;
    fired.current = true;

    (async () => {
      try {
        const token = await getToken();
        const res = await fetch("/api/session-status", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.firstLogin) trackFirstLogin();
        if (data.secondSession) trackSecondSession();
      } catch {
        // Analytics darf nie die App blockieren -- stiller Fehlschlag genügt
      }
    })();
  }, [isSignedIn, getToken]);

  return null;
}

const TOOL_ROUTES: { prefix: string; name: string }[] = [
  { prefix: "/finanzierung-simpel", name: "finanzierung_simpel" },
  { prefix: "/finanzierungsvergleich", name: "finanzierungsvergleich" },
  { prefix: "/finanzierung", name: "finanzierung" },
  { prefix: "/wohnung", name: "eigentumswohnung" },
  { prefix: "/mfh", name: "mfh" },
  { prefix: "/einfamilienhaus", name: "einfamilienhaus" },
  { prefix: "/gemischte-immobilie", name: "mixed_use" },
  { prefix: "/gewerbe", name: "gewerbe" },
  { prefix: "/miete", name: "mietkalkulation" },
  { prefix: "/vergleich", name: "compare" },
  { prefix: "/afa", name: "afa_rechner" },
];

function ToolUsageTracker() {
  const location = useLocation();
  const { isSignedIn } = useUser();
  const lastTracked = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!isSignedIn) return;
    const path = location.pathname;
    if (path === lastTracked.current) return;
    const match = TOOL_ROUTES.find((r) => path.startsWith(r.prefix));
    if (match) {
      lastTracked.current = path;
      trackToolUsed(match.name);
    }
  }, [location.pathname, isSignedIn]);

  return null;
}

// -------------------------------------------------------------
// Guards
// -------------------------------------------------------------
function RequireLogin({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useUser();
  const location = useLocation();
  if (!isLoaded) return <LoadingScreen />;
  // Paket 36 (Dominik 22.10.2026): Wer aus einer Mail auf /deal/:id?tab=… kommt und erst
  // einloggen muss, landet danach wieder genau dort – nicht auf „Meine Objekte“.
  return isSignedIn ? <>{children}</> : <Navigate to={loginZiel(location.pathname, location.search)} replace />;
}

// -------------------------------------------------------------
// Dashboard
// -------------------------------------------------------------
function AppInner() {
  const { isSignedIn, isLoaded } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  // Einmal pro App-Mount: UTMs/fbclid/gclid/Landingpage zwischenspeichern (siehe
  // PHASE-0-BEFUND.md Abschnitt 2) -- faengt den mehrstufigen Schnell-Check-Flow ab,
  // bei dem /register die urspruenglichen Query-Parameter sonst nicht mehr saehe.
  useLandingAttributionCache();

  // Globale Absicherung fuer den Schnell-Check (Live-Befund 10.10.2026): Clerk landet nach
  // der Registrierung nicht zuverlaessig auf next=/check (siehe Register.tsx, forceRedirectUrl-
  // Kommentar dort) -- egal auf welcher Seite der frisch eingeloggte Nutzer tatsaechlich
  // landet, wird hier ein offener Entwurf erkannt und dorthin weitergeleitet. /check selbst
  // macht danach das eigentliche Speichern/Claimen (siehe Check.tsx) -- hier wird nur
  // umgeleitet, nie selbst gespeichert, damit kein doppelter Speichervorgang entstehen kann.
  // Gleiches Muster wie pending_checkout_plan (siehe Dashboard-Komponente unten), nur auf
  // App-Root-Ebene statt an eine bestimmte Route gebunden, weil next hier nicht zuverlaessig ist.
  React.useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (location.pathname === "/check" || location.pathname === "/anmelden" || location.pathname.startsWith("/logout")) return;
    // Paket 30: geparkter Entwurf (FREE-Grenze) leitet nicht um – sonst führte
    // „Oder ein altes Objekt löschen“ von /check direkt wieder nach /check.
    const entwurf = loadQuickCheckDraft();
    if (entwurf && !entwurf.geparkt) navigate("/check", { replace: true });
  }, [isLoaded, isSignedIn, location.pathname, navigate]);

  return (
    <div className="min-h-screen" style={{ background: "#f5f3ea" }}>
      <SeitenMeta />
      <CheckoutRefresh />
      <SignupTracker />
      <SessionTracker />
      <ToolUsageTracker />
      <RouteChangeTracker />
      <ConsentBanner />

      <Suspense fallback={location.pathname.startsWith("/register") ? <RegisterSkeleton /> : <LoadingScreen />}>
        <RouteErrorBoundary>
          <Routes>
            <Route path="/" element={<StartUmleitung ladeanzeige={<LoadingScreen />} />} />
            <Route path="/changelog" element={<AppShell><Changelog /></AppShell>} />

            {/* Wohnung: kostenlos, aber Login nötig */}
            <Route
              path="/wohnung"
              element={
                <AnalyzerOderCheck objektart="etw">
                  <RequireLogin>
                    <AppShell>
                      <Eigentumswohnung />
                    </AppShell>
                  </RequireLogin>
                </AnalyzerOderCheck>
              }
            />

            {/* Jedes Tool ist für jeden eingeloggten Nutzer voll zugänglich -- nur
                einzelne Features innerhalb sind PRO-gegatet (siehe ProGate in den
                jeweiligen Analyzer-Komponenten). Route-Level-Guards prüfen daher nur
                noch Login, nicht mehr Plan. */}
            <Route
              path="/mfh"
              element={
                <AnalyzerOderCheck objektart="mfh">
                  <RequireLogin>
                    <AppShell>
                      <MFHCheck />
                    </AppShell>
                  </RequireLogin>
                </AnalyzerOderCheck>
              }
            />
            <Route
              path="/miete"
              element={
                <RequireLogin>
                  <AppShell>
                    <Mietkalkulation />
                  </AppShell>
                </RequireLogin>
              }
            />
            <Route
              path="/finanzierung-simpel"
              element={
                <RequireLogin>
                  <AppShell>
                    <FinanzierungSimple />
                  </AppShell>
                </RequireLogin>
              }
            />
            <Route
              path="/finanzierungsvergleich"
              element={
                <RequireLogin>
                  <AppShell>
                    <FinanzierungsVergleich />
                  </AppShell>
                </RequireLogin>
              }
            />

            <Route
              path="/einfamilienhaus"
              element={
                <AnalyzerOderCheck objektart="efh">
                  <RequireLogin>
                    <AppShell>
                      <Einfamilienhaus />
                    </AppShell>
                  </RequireLogin>
                </AnalyzerOderCheck>
              }
            />
            <Route
              path="/gemischte-immobilie"
              element={
                <AnalyzerOderCheck objektart="mixeduse">
                  <RequireLogin>
                    <AppShell>
                      <MixedUseCheck />
                    </AppShell>
                  </RequireLogin>
                </AnalyzerOderCheck>
              }
            />
            <Route path="/mixed-use" element={<Navigate to="/gemischte-immobilie" replace />} />
            <Route path="/deals" element={<RequireLogin><AppShell><Portfolio /></AppShell></RequireLogin>} />
            {/* Paket 50: 2–4 gespeicherte Objekte nebeneinander. */}
            <Route path="/deals/vergleich" element={<RequireLogin><AppShell><Objektvergleich /></AppShell></RequireLogin>} />
            {/* /portfolio: bisherige Route, bleibt als Weiterleitung fuer bestehende Links/Lesezeichen.
                Spaeter fuer tatsaechlich gekaufte Objekte vorgesehen -- dann eigene Seite, kein Redirect mehr. */}
            <Route path="/portfolio" element={<Navigate to="/deals" replace />} />
            {/* Gefuehrter Ablauf nach /check (PHASE-4-BEFUND.md Runde 1): helles Design
                innerhalb der Seite (DealShell). Nachtrag 15.10.2026 (Dominiks Vorgabe,
                Punkt 4): jetzt in der AppShell (Seitenleiste, aktueller Deal hervorgehoben)
                statt wie urspruenglich bewusst ohne sie. */}
            <Route path="/deal/:id" element={<RequireLogin><AppShell><DealDetail /></AppShell></RequireLogin>} />
            <Route
              path="/gewerbe"
              element={
                <AnalyzerOderCheck objektart="gewerbe">
                  <RequireLogin>
                    <AppShell>
                      <GewerbeCheck />
                    </AppShell>
                  </RequireLogin>
                </AnalyzerOderCheck>
              }
            />
            <Route
              path="/vergleich"
              element={
                <PlanGuard required="pro">
                  <AppShell>
                    <Compare />
                  </AppShell>
                </PlanGuard>
              }
            />
            <Route
              path="/afa"
              element={
                <PlanGuard required="pro">
                  <AppShell>
                    <AfaRechner />
                  </AppShell>
                </PlanGuard>
              }
            />
            {/* Alte „Finanzierungs-Analyse“ (nirgends mehr verlinkt) -> Finanzierungsrechner (18.10.2026). */}
            <Route path="/finanzierung" element={<Navigate to="/finanzierung-simpel" replace />} />

            {/* Pricing / Checkout / Upgrade */}
            <Route path="/preise" element={<Pricing />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/upgrade" element={<Upgrade />} />
            <Route path="/extension" element={<RequireLogin><AppShell><Extension /></AppShell></RequireLogin>} />

            {/* Clerk-Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* Paket 56: Ziel des Links aus der Anmelde-Mail */}
            <Route path="/anmelden" element={<AnmeldenPerLink />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/account" element={<RequireLogin><AppShell><Account /></AppShell></RequireLogin>} />

            {/* Diagnose */}
            <Route path="/authprobe" element={<AuthProbe />} />
            {/* Design-Grundlage: unverlinkt, nur per direkter URL */}
            <Route path="/design-preview" element={<DesignPreview />} />
            {/* Schnell-Check: oeffentlich, kein RequireLogin, unverlinkt (siehe Check.tsx) */}
            <Route path="/check" element={<Check />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </RouteErrorBoundary>
      </Suspense>

      {/* Propora Assistent – global floating, außer auf Register/Login/Checkout */}
      {!ASSISTENT_HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p)) && (
        <Suspense fallback={null}>
          <PropraAssistent />
        </Suspense>
      )}
    </div>
  );
}

/** Titel und Meta-Description je Route (Paket 22). Steht vor RouteChangeTracker, damit der
 *  page_view schon den neuen Titel sieht (Effekte von Geschwistern laufen in Reihenfolge). */
function SeitenMeta() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    setzeSeitenMeta(seitenMetaFuer(pathname));
  }, [pathname]);
  return null;
}

export default function App() {
  return <AppInner />;
}
